/**
 * @namespace source.folder
 */
/**
 * @typedef Folder
 * @type Object
 * @property {string} id
 * @property {string} name
 * @property {string} mediaType
 * @property {number} filesCount
 * @property {date} createdAt
 */
import { toFolder } from './mappers/folder';
import { toError } from './parsers/error';
import { validateNewProps } from './validators/new-props';
import { toErrorObject } from '../../utils/mappers';
export function initFolder(settings, http) {
  function mapFolder(res, token) {
    res.data = toFolder({
      ...res.data,
      token
    }, settings);
    return res;
  }

  /**
   * @summary Create a new folder with given name and parameters
   * @memberof source.folder
   * @param {string} folderName
   * @param {object} options
   * @param {string} options.mediaType
   * @param {string} options.parentId
   * @returns {Promise}
   * @fulfill {Folder} New folder object
   * @reject {Error}
   */
  function create(folderName, options) {
    if (!folderName) {
      return toError(toErrorObject({
        status: -200,
        statusText: 'Internal JS Error',
        data: {
          message: 'Please specify name'
        }
      }));
    }
    options = options || {};
    const url = `${settings.apiUrl}/site/media/folders`;
    const data = {
      folder_name: folderName,
      parent_folder_id: options.parentId || null
    };
    if (settings.namespace) {
      data.ns = settings.namespace;
    }
    const postOptions = {
      headers: options.headers || {}
    };
    return http.post(url, data, null, postOptions).then(mapFolder).catch(toError);
  }

  /**
   * @summary Returns folder
   * @memberof source.folder
   * @param {String} folderId
   * @param {Object} [options]
   * @returns {Promise}
   * @fulfill {Item}
   * @reject {Error}
   */
  function getRequest(folderId, options) {
    options = options || {};
    const queryParams = {};
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.get(`${settings.apiUrl}/site/media/folders/${folderId}`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(function (res) {
      return mapFolder(res, options.thumbnails, options.siteMediaToken);
    }).catch(toError);
  }

  /**
   * @summary Updates the folder with given diff object
   * @memberof source.folder
   * @param {Folder} folder Current folder object that should be updated
   * @param {object} newFolderProps
   * @param {object} options
   * @returns {Promise}
   * @fulfill {Folder} Modified folder object
   * @reject {Error}
   */
  function update(folder, newFolderProps, options) {
    const arePropsValid = validateNewProps(newFolderProps);
    if (arePropsValid.valid === false) {
      return toError(toErrorObject({
        status: -200,
        statusText: 'Internal JS Error',
        data: {
          message: arePropsValid.message
        }
      }));
    }
    const data = {};
    if (newFolderProps.name) {
      data.folder_name = newFolderProps.name;
    }
    if (newFolderProps.parentId || newFolderProps.parentId === null) {
      data.parent_folder_id = newFolderProps.parentId;
    }
    if (settings.namespace) {
      data.ns = settings.namespace;
    }
    options = options || {};
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    const url = `${settings.apiUrl}/site/media/folders/${folder.id}`;
    const postOptions = {
      headers: options.headers || {}
    };
    return http.put(url, data, null, postOptions).then(function (res) {
      res.data = {
        ...folder,
        ...newFolderProps
      };
      return res;
    }).catch(toError);
  }
  return {
    create,
    get: getRequest,
    update
  };
}